import { useState, useEffect, useCallback } from "react";

const useOrders = () => {
  const [orders, setOrders] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);

  // Memoize fetchOrders to prevent recreation on every render
  const fetchOrders = useCallback(async () => {
    try {
      setFetchLoading(true);
      setFetchError(null);
      console.log("Fetching orders...");

      const response = await fetch("https://inaaniserver-50c4305cc7f7.herokuapp.com/order/get_all_order");

      if (!response.ok) {
        throw new Error(`HTTP Error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response (Fetch):", result);

      if (result.status) {
        const fixedOrders = result.data.map(order => {
          console.log("Order from API:", order);
          return {
            ...order,
            quantity: order.quantity?.data
              ? parseInt(order.quantity.data.toString(), 10)
              : Number(order.quantity) || 0,
            // Ensure other fields are present or provide defaults
            product_id: order.product_id || "N/A",
            status: order.status || "N/A",
          };
        });

        setOrders(fixedOrders);
      } else {
        throw new Error(result.message || "Failed to fetch orders");
      }
    } catch (err) {
      console.error("Fetch Error:", err.message);
      setFetchError(err.message);
    } finally {
      setFetchLoading(false);
    }
  }, []); // Empty dependency array ensures fetchOrders is stable

  // Fetch orders on mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]); // Depend on fetchOrders to ensure it runs when memoized function changes

  // Memoize updateOrder to prevent recreation
  const updateOrder = useCallback(async (orderData) => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);
      setUpdateStatus(null);
      console.log("Updating order with data:", orderData);

      const response = await fetch("https://inaaniserver-50c4305cc7f7.herokuapp.com/order/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: orderData.id,
          product_id: orderData.product_id,
          user_id: orderData.user_id,
          quantity: orderData.quantity,
          price: orderData.price,
          total_amount: orderData.quantity * orderData.price,
          address: orderData.address,
          status: orderData.status,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Update API Response (Full):", JSON.stringify(result, null, 2));

      if (result.error === false && result.message === "Successfully updated.") {
        setUpdateStatus("updated_successfully");
        await fetchOrders(); // Refresh orders after update
        return { success: true, message: "Successfully updated." };
      } else if (result.error === true) {
        throw new Error(result.message || "Failed to update order");
      } else {
        throw new Error("Unexpected response: " + JSON.stringify(result));
      }
    } catch (err) {
      console.error("Update Error:", err.message);
      setUpdateError(err.message);
      setUpdateStatus("error");
      return { success: false, error: err.message };
    } finally {
      setUpdateLoading(false);
    }
  }, [fetchOrders]); // Empty dependency array ensures updateOrder is stable

  const isLoading = fetchLoading || updateLoading;
  const error = fetchError || updateError;

  return {
    orders,
    isLoading,
    error,
    fetchLoading,
    fetchError,
    updateOrder,
    updateLoading,
    updateError,
    updateStatus,
    refetch: fetchOrders, // Stable refetch function
  };
};

export default useOrders;